/**
 * URL Sanitization Utilities
 * Provides security functions for validating and sanitizing URLs
 */

/**
 * List of allowed protocols for external URLs
 */
const ALLOWED_PROTOCOLS = ["http:", "https:"];

/**
 * List of allowed domains for external navigation
 * Add trusted domains here
 */
const ALLOWED_EXTERNAL_DOMAINS = [
    "discord.gg",
    "discord.com",
    "devourplay.io",
    "devourgo.io",
    "cdn.devourgo.io",
    "cdn.devourplay.io",
];

/**
 * Validates if a URL is safe to use
 * @param url - The URL to validate
 * @returns true if the URL is safe, false otherwise
 */
export function isValidUrl(url: string): boolean {
    if (!url) return false;

    try {
        const parsed = new URL(url);

        // Check protocol
        if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) {
            console.warn(`Invalid protocol in URL: ${parsed.protocol}`);
            return false;
        }

        // For external URLs, check if domain is allowed
        if (parsed.hostname && !isInternalUrl(url)) {
            const isAllowed = ALLOWED_EXTERNAL_DOMAINS.some(domain =>
                parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`));

            if (!isAllowed) {
                console.warn(`External domain not allowed: ${parsed.hostname}`);
                return false;
            }
        }

        return true;
    } catch (error) {
        console.warn(`Invalid URL format: ${url}`, error);
        return false;
    }
}

/**
 * Checks if a URL is internal (relative or same origin)
 * @param url - The URL to check
 * @returns true if the URL is internal
 */
export function isInternalUrl(url: string): boolean {
    if (!url) return false;

    // Relative URLs are always internal
    if (url.startsWith("/") && !url.startsWith("//")) {
        return true;
    }

    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.origin === window.location.origin;
    } catch {
        return false;
    }
}

/**
 * Sanitizes a URL for safe use
 * @param url - The URL to sanitize
 * @param fallback - Fallback URL if sanitization fails (defaults to '/')
 * @returns Sanitized URL or fallback
 */
export function sanitizeUrl(url: string, fallback: string = "/"): string {
    if (!url) return fallback;

    // For relative URLs, just ensure they start with /
    if (url.startsWith("/") && !url.startsWith("//")) {
        return url;
    }

    // For absolute URLs, validate them
    if (isValidUrl(url)) {
        return url;
    }

    console.warn(`URL failed sanitization, using fallback: ${url}`);
    return fallback;
}

/**
 * Sanitizes a URL for use in CSS (e.g., background-image)
 * @param url - The URL to sanitize
 * @returns Sanitized URL safe for CSS or empty string
 */
export function sanitizeCssUrl(url: string): string {
    if (!url) return "";

    // Remove any potential CSS injection
    const cleanUrl = url.replace(/[()'"]/g, "");

    // Validate the cleaned URL
    if (isValidUrl(cleanUrl)) {
        return cleanUrl;
    }

    return "";
}

/**
 * Creates a safe external link props object
 * @param url - The URL to link to
 * @returns Props object for safe external linking
 */
export function getExternalLinkProps(url: string): Record<string, string> {
    if (!isValidUrl(url)) {
        console.warn(`Invalid external URL: ${url}`);
        return {};
    }

    return {
        href: url,
        target: "_blank",
        rel: "noopener noreferrer", // Security best practice for external links
    };
}